import { fmtDate } from '@dcc-erp/shared';
import type { LvaEventType, LvaMovementListRow } from '@dcc-erp/shared';

import { movementApproverDisplay } from './movement-display';

// ============================================================
// LVA movement audit panel — the immutable, chronological event stream for one
// LVA item. DOM/tone copied from the shared AuditLog (Component 7), but the
// label/tone maps are LvaEventType-typed (AuditLog's are ApprovalEventType).
// Presentational only — the parent (DetailModal) wires the data via
// useLvaMovements.
// ============================================================

const EVENT_LABEL: Record<LvaEventType, string> = {
  TRANSFER: 'โอนระหว่างหน่วยงาน',
  REASSIGN_CUSTODIAN: 'เปลี่ยนผู้ถือครอง',
  MARK_DAMAGED: 'ชำรุด',
  MARK_LOST: 'สูญหาย',
  DISPOSE: 'จำหน่าย',
};

const TONE: Partial<Record<LvaEventType, string>> = {
  TRANSFER: 'var(--text)',
  REASSIGN_CUSTODIAN: 'var(--text)',
  MARK_DAMAGED: 'var(--warning)',
  MARK_LOST: 'var(--negative)',
  DISPOSE: 'var(--negative)',
};

function fmtTime(d: string | Date): string {
  const dt = d instanceof Date ? d : new Date(d);
  return `${fmtDate(dt)} ${dt.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}`;
}

// Build the "from → to" line for whichever dimension the event moved. Branch
// and custodian are the two dimensions surfaced on a movement row; if neither
// resolved name is present (e.g. a mark-damaged that changes no dimension) we
// render nothing.
function fromToLine(e: LvaMovementListRow): string | null {
  if (e.fromBranchName || e.toBranchName) {
    return `${e.fromBranchName ?? '-'} → ${e.toBranchName ?? '-'}`;
  }
  if (e.fromCustodianName || e.toCustodianName) {
    return `${e.fromCustodianName ?? '-'} → ${e.toCustodianName ?? '-'}`;
  }
  return null;
}

export function LvaMovementLog({ events }: { events: LvaMovementListRow[] }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
        ประวัติการดำเนินการ
      </div>
      {events.length === 0 ? (
        <div className="meta">ยังไม่มีประวัติ</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {events.map((e) => {
            const line = fromToLine(e);
            const approver = movementApproverDisplay(e);
            return (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: '#fafafa',
                }}
              >
                <div style={{ minWidth: 120, fontSize: 12, color: 'var(--text-muted)' }}>
                  {fmtTime(e.createdAt)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: TONE[e.eventType] ?? 'var(--text)' }}>
                      {EVENT_LABEL[e.eventType] ?? e.eventType}
                    </span>
                    <span className="meta" style={{ marginLeft: 6 }}>
                      {e.code}
                    </span>
                  </div>
                  <div className="meta">
                    {e.submittedBy}
                    {approver ? ` · อนุมัติ: ${approver}` : ''}
                  </div>
                  {line && (
                    <div className="meta" style={{ fontSize: 12 }}>
                      {line}
                    </div>
                  )}
                  {e.reason && (
                    <div className="meta" style={{ fontStyle: 'italic' }}>
                      &ldquo;{e.reason}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
